import { useCallback } from 'react';

import type { SidekickArtifactDownloadedCallback } from '../components/AISidekickPanel/SidekickPanelContext';
import type { SidekickFeatureEventCallback } from '../featureEvents';
import type { SidekickLifecycleCallback } from '../lifecycle';
import {
  SIDEKICK_ARTIFACT_HEAP_EVENTS,
  SIDEKICK_FEATURE_HEAP_EVENTS,
  SIDEKICK_LIFECYCLE_HEAP_EVENTS,
} from './names';
import {
  getSidekickArtifactHeapProperties,
  getSidekickFeatureHeapProperties,
  getSidekickLifecycleHeapProperties,
  type SidekickHeapContext,
} from './properties';
import { trackSidekickHeapEvent } from './trackSidekickHeapEvent';

/** Callbacks that send Sidekick's events to Heap, for the Sidekick shells. */
export type SidekickHeapEventHandlers = {
  onArtifactDownloaded: SidekickArtifactDownloadedCallback;
  onLifecycleEvent: SidekickLifecycleCallback;
  onFeatureUsed: SidekickFeatureEventCallback;
};

/**
 * Sends Sidekick's downloads, lifecycle, and feature events to Heap under
 * the shared `CS Sidekick` names. The callbacks change only when a context
 * value does.
 * @param context - Organization and fleet Sidekick is showing.
 * @returns Callbacks to hand the Sidekick shell.
 */
export const useSidekickHeapEvents = ({
  organizationId,
  organizationName,
  fleetId,
  isEmbedded,
  platform,
}: SidekickHeapContext): SidekickHeapEventHandlers => {
  const onArtifactDownloaded = useCallback<SidekickArtifactDownloadedCallback>(
    (event) => {
      trackSidekickHeapEvent(
        SIDEKICK_ARTIFACT_HEAP_EVENTS[event.artifactType],
        getSidekickArtifactHeapProperties(event, {
          organizationId,
          fleetId,
          isEmbedded,
        })
      );
    },
    [fleetId, isEmbedded, organizationId]
  );

  const onLifecycleEvent = useCallback<SidekickLifecycleCallback>(
    (event) => {
      trackSidekickHeapEvent(
        SIDEKICK_LIFECYCLE_HEAP_EVENTS[event.type],
        getSidekickLifecycleHeapProperties(event, {
          organizationId,
          organizationName,
          fleetId,
          isEmbedded,
          platform,
        })
      );
    },
    [fleetId, isEmbedded, organizationId, organizationName, platform]
  );

  const onFeatureUsed = useCallback<SidekickFeatureEventCallback>(
    (event) => {
      trackSidekickHeapEvent(
        SIDEKICK_FEATURE_HEAP_EVENTS[event.type],
        getSidekickFeatureHeapProperties(event, {
          organizationId,
          organizationName,
          fleetId,
          isEmbedded,
        })
      );
    },
    [fleetId, isEmbedded, organizationId, organizationName]
  );

  return { onArtifactDownloaded, onLifecycleEvent, onFeatureUsed };
};
